import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CategoryCard from '@/components/CategoryCard';
import BookCard from '@/components/BookCard';
import { apiClient } from '@/services/api';
import { Category, Book } from '@/types';

const Home: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [featuredBook, setFeaturedBook] = useState<Book | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [categoriesData, booksData] = await Promise.all([
          apiClient.getCategories(),
          apiClient.getBooks(),
        ]);
        setCategories(categoriesData);
        const featured = booksData.find(
          (book) =>
            book.title.toLowerCase().includes('as a man') ||
            book.title.toLowerCase().includes('think')
        );
        setFeaturedBook(featured || booksData[0] || null);
      } catch (error) {
        console.error('Error loading home data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative py-28 sm:py-36 text-center overflow-hidden"
        style={{ background: 'var(--bg-hero)' }}
      >
        {/* Subtle decorative elements */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, var(--accent-primary) 0%, transparent 50%),
                              radial-gradient(circle at 80% 50%, var(--accent-secondary) 0%, transparent 50%)`,
          }}
        />

        <div className="relative max-w-3xl mx-auto px-8">
          {/* New Feature Announcement Badge */}
          <Link
            to="/oracle"
            className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-xs font-semibold mb-10 transition-all border hover:scale-[1.02] hover:shadow-lg"
            style={{
              background: 'var(--accent-light)',
              borderColor: 'var(--accent-primary)',
              color: 'var(--accent-primary)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent-primary)] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--accent-primary)]"></span>
            </span>
            <span>New Feature: Draw Daily Guidance from the Mystic Oracle</span>
            <span style={{ fontSize: '0.85rem', lineHeight: '1' }}>✦</span>
          </Link>
          <p
            className="text-xs font-semibold tracking-[0.25em] uppercase mb-6"
            style={{ color: 'var(--text-secondary)' }}
          >
            Audio Library
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold mb-7 leading-[1.15]"
            style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-primary)' }}
          >
            Listen to the wisdom
            <br />
            of the ages
          </h1>
          <p
            className="text-lg max-w-xl mx-auto mb-12 leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}
          >
            20 timeless books, beautifully narrated and organized for the modern seeker.
            Free and in the public domain.
          </p>
          <a href="#categories" className="btn-primary">
            Start Exploring
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        </div>
      </section>

      {/* Categories Section */}
      <section className="max-w-5xl mx-auto px-8 py-20">
        <div className="text-center mb-14">
          <p
            className="text-xs font-semibold tracking-[0.2em] uppercase mb-3"
            style={{ color: 'var(--accent-primary)' }}
          >
            Browse by theme
          </p>
          <h2
            id="categories"
            className="text-2xl sm:text-3xl font-bold"
            style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-primary)' }}
          >
            Five paths to explore
          </h2>
        </div>

        {isLoading ? (
          <div className="flex flex-wrap justify-center gap-6">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-44 rounded-2xl animate-pulse w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]"
                style={{ background: 'var(--bg-secondary)' }}
              />
            ))}
          </div>
        ) : categories.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-6">
            {categories.map((category) => (
              <div key={category.id} className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]">
                <CategoryCard category={category} />
              </div>
            ))}
          </div>
        ) : (
          <div
            className="text-center py-16 rounded-2xl border border-dashed"
            style={{ borderColor: 'var(--border)' }}
          >
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Categories will appear here once the backend is connected.
            </p>
          </div>
        )}
      </section>

      {/* Featured Book Section */}
      {featuredBook && (
        <section className="max-w-5xl mx-auto px-8 pb-24">
          <div
            className="rounded-2xl p-10 sm:p-14"
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
            }}
          >
            <p
              className="text-xs font-semibold tracking-[0.2em] uppercase mb-8"
              style={{ color: 'var(--accent-primary)' }}
            >
              Featured
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 items-center">
              <div className="sm:col-span-2">
                <h3
                  className="text-3xl font-bold mb-2"
                  style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-primary)' }}
                >
                  {featuredBook.title}
                </h3>
                <p className="mb-5" style={{ color: 'var(--text-secondary)' }}>
                  by {featuredBook.author} ({featuredBook.year_published})
                </p>
                <p className="mb-8 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  {featuredBook.description}
                </p>
                <div className="flex gap-10 mb-8">
                  <div>
                    <p
                      className="text-xs font-medium uppercase tracking-wider mb-1"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      Duration
                    </p>
                    <p className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {Math.round(featuredBook.total_duration_seconds / 3600)}h
                    </p>
                  </div>
                  <div>
                    <p
                      className="text-xs font-medium uppercase tracking-wider mb-1"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      Chapters
                    </p>
                    <p className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {featuredBook.chapter_count}
                    </p>
                  </div>
                </div>
                <Link to={`/book/${featuredBook.id}`} className="btn-primary">
                  Start Listening
                </Link>
              </div>
              <div className="flex items-center justify-center">
                {featuredBook.cover_image_url ? (
                  <img
                    src={featuredBook.cover_image_url}
                    alt={featuredBook.title}
                    className="rounded-xl w-full max-w-xs"
                    style={{ boxShadow: 'var(--shadow-lg)' }}
                  />
                ) : (
                  <div
                    className="w-full max-w-xs aspect-[3/4] rounded-xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                      boxShadow: 'var(--shadow-lg)',
                    }}
                  >
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Mystic Oracle CTA Section */}
      <section className="max-w-5xl mx-auto px-8 pb-24">
        <div
          className="rounded-2xl p-10 sm:p-14 text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1e1e38, #2a1a3e, #1a2e3e)',
            border: '1px solid var(--border)',
          }}
        >
          <div
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage: 'url(/spiritual-pattern.svg)',
              backgroundRepeat: 'repeat',
              backgroundSize: '1000px 1000px',
            }}
          />
          <div className="relative z-10 max-w-xl mx-auto">
            <p
              className="text-xs font-semibold tracking-[0.25em] uppercase mb-4"
              style={{ color: 'var(--accent-primary)' }}
            >
              Interactive Spiritual Practice
            </p>
            <h3
              className="text-3xl font-bold mb-5"
              style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-primary)' }}
            >
              The Mystic Oracle Decks
            </h3>
            <p className="mb-10 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Draw your daily guidance from our parallel translation decks: the Coptic Gospel of Thomas, the Gospel of Mary, and the beautiful Sufi verses of Rumi.
            </p>
            <Link to="/oracle" className="btn-primary inline-flex items-center gap-2">
              Draw a Card
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
